/**
 * The LaundryPro brand mark: a folded sheet.
 *
 * Structurally this is the sibling of BarberPro's scissors mark — two mirrored
 * halves either side of a centre line, animated by a single numeric prop, built
 * entirely from SVG paths with no external asset, and deliberately geometric
 * rather than illustrative.
 *
 * Where the scissors CLOSE, the fabric FOLDS. The fold is simulated with
 * `scaleX`: a panel rotating away from the viewer foreshortens, so scaling its
 * width from 1 to nearly 0 reads as a fold without needing a 3D transform. The
 * centre line the halves meet on then becomes the crease the entrance screen
 * splits along — the same choreographic beat as the scissors' cut.
 *
 * Like the scissors mark, this does NOT consume the owner's brand colour. Owner
 * colour paints surfaces around the mark; the mark keeps a fixed palette. That
 * is what stops a rebrand from ever making the mark itself look wrong.
 */
export function LaundryMark({
  size = 96,
  /** 1 = fully open (flat), 0 = fully folded. */
  open = 1,
  className = "",
  idleMotion = false,
  glow = false,
}: {
  size?: number;
  open?: number;
  className?: string;
  idleMotion?: boolean;
  glow?: boolean;
}) {
  // Never reaches 0: a zero-width panel disappears entirely and the fold reads
  // as the mark vanishing rather than closing.
  const foldScale = 0.13 + Math.max(0, Math.min(1, open)) * 0.87;

  const panelTransition = "transform 220ms cubic-bezier(0.4,0,0.2,1)";

  return (
    <svg
      viewBox="0 0 120 120"
      width={size}
      height={size}
      className={`${idleMotion ? "lp-mark-idle" : ""} ${glow ? "lp-mark-glow" : ""} ${className}`}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        {/* Woven-cloth sheen. Tonal range matches the reference mark's metal
            gradient, so the two marks sit at the same visual weight. */}
        <linearGradient id="lp-cloth" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8A857E" />
          <stop offset="35%" stopColor="#C4BEB5" />
          <stop offset="58%" stopColor="#7E7972" />
          <stop offset="100%" stopColor="#4A4640" />
        </linearGradient>
      </defs>

      {/* Right half of the sheet, folding toward the crease. */}
      <g
        style={{
          transform: `translate(60px,60px) scaleX(${foldScale})`,
          transformOrigin: "0 0",
          transition: panelTransition,
        }}
      >
        <path
          d="M 0,-44 L 34,-36 Q 40,-34 40,-28 L 40,28 Q 40,34 34,36 L 0,44 Z"
          fill="url(#lp-cloth)"
          stroke="#1C1917"
          strokeWidth="0.6"
        />
        {/* Two pressed creases — what makes it read as folded cloth rather than
            a plain quadrilateral. */}
        <path d="M 10,-32 L 10,32" stroke="#1C1917" strokeWidth="0.5" opacity="0.18" fill="none" />
        <path d="M 24,-26 L 24,26" stroke="#1C1917" strokeWidth="0.5" opacity="0.12" fill="none" />
      </g>

      {/* Left half — the same path, mirrored, exactly as the reference mirrors
          its second blade. One definition, drawn twice. */}
      <g
        style={{
          transform: `translate(60px,60px) scaleX(${-foldScale})`,
          transformOrigin: "0 0",
          transition: panelTransition,
        }}
      >
        <path
          d="M 0,-44 L 34,-36 Q 40,-34 40,-28 L 40,28 Q 40,34 34,36 L 0,44 Z"
          fill="url(#lp-cloth)"
          stroke="#1C1917"
          strokeWidth="0.6"
        />
        <path d="M 10,-32 L 10,32" stroke="#1C1917" strokeWidth="0.5" opacity="0.18" fill="none" />
        <path d="M 24,-26 L 24,26" stroke="#1C1917" strokeWidth="0.5" opacity="0.12" fill="none" />
      </g>

      {/* The crease. Sits above both halves so it stays visible as they fold —
          this is the line the entrance screen splits along. */}
      <path d="M 60,14 L 60,106" stroke="#C8A24A" strokeWidth="2.2" strokeLinecap="round" opacity="0.9" />
      <circle cx="60" cy="60" r="4.2" fill="#C8A24A" />
    </svg>
  );
}
